import { useEffect } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

/**
 * Focuses the first field in error once the render that ties it to its message
 * has landed. react-hook-form's own focus (`shouldFocusError`, to turn off on the
 * form) runs before that render, so a screen reader read the field without the
 * aria-describedby naming its error (BBS-438).
 *
 * Leaving the field first makes a repeated submit from the keyboard, focus
 * already in the field, read it again.
 */
export function useFocusFirstError<T extends FieldValues>(
  form: Pick<UseFormReturn<T>, "formState" | "setFocus">,
  fieldsInOrder: readonly Path<T>[],
) {
  const { errors, submitCount } = form.formState;

  useEffect(() => {
    const firstInError = fieldsInOrder.find(
      (name) => (errors as Record<string, unknown>)[name],
    );
    if (!firstInError) return;
    (document.activeElement as HTMLElement | null)?.blur();
    form.setFocus(firstInError);
    // Once per submit: the errors of that submit are committed with its count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);
}
